/**
 * アカウントの設定の画面（pages/AccountPage.tsx: 開発者アカウントの閉鎖）の英語の文言。en.ts が `...enAccount` で取り込む
 * （ja.account.ts が同じキーを全部持つことを型で強制）。
 *
 * WHY 別ファイルか: en.ts が 650 行を超えている。閉鎖の説明は「必ず全部見せる 3 つ」（管理判断 2026-09-27）をまとめて
 * 見直せるようにしておきたい（サイトの説明・CLI の文言と食い違わせない）。
 *
 * 見せる 3 つ（docs/teams/creator/tasks/account-close.md）: ①再開できない ②前払いの残高の扱い（売上由来 → 出金できる残高・
 * カードで入れた分 → カードへ一部返金）③出金の期限（Kumo 側は無い・長く放置すると州の未請求財産の報告の対象になりうる）
 */
export const enAccount = {
  'account.topbarLink': 'Account',
  'account.eyebrow': 'Your account',
  'account.title': 'Account settings',
  'account.signedInAs': 'Signed in as {email}',

  'account.close.title': 'Close your account',
  'account.close.lead': 'Read all three points before you close. Closing is for when you are done with {product} for good.',
  // 出金を閉じている間は 3 つめ（出金の期限）を出さない = 2 つ
  'account.close.leadLaunch': 'Read both points before you close. Closing is for when you are done with {product} for good.',
  'account.close.p1Title': '1. It cannot be reopened',
  'account.close.p1Body':
    'A closed account cannot be reopened — there is no way back, not even through support. To use {product} again you would create a new account (the same email address can be used again only after this account has been erased).',
  'account.close.p2Title': '2. Your prepaid balance is returned',
  'account.close.p2Body':
    "Unused prepaid credit is returned after this month's usage is finalized (by around the 3rd of next month). The part that came from your earnings goes back to your withdrawable balance. The part you added by card is refunded to that card (a partial refund of the original payment). Automatic top-ups stop.",
  // 出金を閉じている間は「売上から入れた分」は無い（前払いへ売上を充てる機能も閉じている）。
  // 閉鎖で実際に起きるのは 2 つ（Money の了承 2026-09-30・walkthrough W1）: ① カードで入れた分はそのカードへ返金
  // ② 招待クレジットの残りは返金されず消える（現金に戻せない・出金できない = MASTER-PLAN §0 の管理判断）。
  // WHY 見出しも差し替えるか: 「残高は返されます」だと招待クレジットも返ると読めてしまう（②と矛盾する）
  'account.close.p2TitleLaunch': '2. What happens to your prepaid balance',
  'account.close.p2BodyLaunch':
    "The rest of the prepaid balance you added by card is refunded to that card after the current month's usage is settled (any unpaid usage is deducted first; the card processing fee from your top-ups is not returned). Any invite credit left is not refunded — it is lost when you close. Automatic top-ups stop.",
  'account.close.p3Title': '3. You can still withdraw — there is no deadline',
  'account.close.p3Body':
    'After closing you can still sign in and withdraw your earnings. {product} sets no deadline, but money left unclaimed for a long time may have to be reported to a US state as unclaimed property.',
  'account.close.afterTitle': 'After closing',
  'account.close.afterCan': 'You can: sign in with the same email and password, see your earnings and withdraw.',
  'account.close.afterCanLaunch': 'You can: sign in with the same email and password.',
  'account.close.afterCannot': 'You cannot: create new projects. You are signed out on every device, including this one, and any AI agent or terminal command you connected is disconnected.',
  'account.close.afterErase': 'Once {product} holds no money for you and every refund is done, your email, password and name are erased. Accounting records are kept as the law requires.',
  'account.close.aiNote': 'Your AI agent cannot close your account. Only you can — here, or with {cmd} in the terminal on your own computer.',

  'account.close.gamesTitle': 'First, delete your projects',
  'account.close.gamesBody':
    '{product} does not delete running projects for you when you close. Delete each project below, then close the account (once a project is deleted, nobody can use that app anymore).',
  'account.close.gamesNone': 'No projects left.',
  'account.close.gamesRemainAlert': 'You still have projects. Delete them first (the list above).',
  'account.close.deleteGame': 'Delete…',
  'account.close.deleteGameTitle': 'Delete {name}?',
  'account.close.deleteGameBody': 'Nobody can use this app anymore, right away (both the key in your app and the server-only keys stop working). This cannot be undone.',
  'account.close.deleteGameSlug': 'In 30 days, the name {slug} in this project\'s web address (and its old names, if you changed it) can be used by someone else. Links you posted on X and elsewhere will then open their page.',
  'account.close.typeSlug': 'Type {slug} to confirm',
  'account.close.deleteGameConfirm': 'Delete project',

  'account.close.formTitle': 'Close the account',
  'account.close.typeConfirm': 'Type your email address ({email}) or {word}',
  'account.close.password': 'Password',
  'account.close.submit': 'Close my account permanently',
  'account.close.wrongPassword': 'That password is incorrect. You are still signed in — try again.',

  'account.done.title': 'Your account is closed',
  'account.done.body': 'You are signed out on every device, including this one. Any AI agent or terminal command you connected is disconnected too.',
  'account.done.withdraw': 'You can still sign in with the same email and password to withdraw your earnings (Money → Withdraw).',
  'account.done.ok': 'OK',

  'account.closed.title': 'This account is closed',
  'account.closed.body': 'Closed on {date}. You can still sign in and withdraw your earnings. New projects cannot be created, and the account cannot be reopened.',
  'account.closed.bodyLaunch': 'Closed on {date}. New projects cannot be created, and the account cannot be reopened.',
  'account.closed.withdraw': 'Go to Withdraw',
  'account.closed.banner': 'This account is closed. You can still withdraw your earnings.',
  'account.closed.bannerLaunch': 'This account is closed. New projects cannot be created.',

  // サーバーの error.code（errorText が `error.<code>` を自動で引く）
  'error.account_closed': 'This account is closed, so new projects cannot be created. You can still withdraw your earnings.',
  // 出金を閉じている間の error.account_closed（i18n/index.ts の errorText が isLaunchHidden('payouts') で差し替える）
  'error.account_closedLaunch': 'This account is closed, so new projects cannot be created.',
  'error.projects_remain': 'You still have projects. Delete them first, then close the account.',
  'error.reauthentication_failed': 'Password is incorrect.'
};
